import { shortVoiceName } from "@/lib/studioApi";
import { Switch } from "@/components/ui/switch";

const MODES = [
  { key: "instrumental", label: "Music only", hint: "Just the instruments under your voice" },
  { key: "partner", label: "Music + duet partner", hint: "Your AI partner sings their parts" },
  { key: "full", label: "Music + AI vocals", hint: "Keep every AI voice (choir, harmonies) and add yours" },
];

/** Backing options this version can offer (duet partner only for duets, AI vocals once separated). */
export function availableModes(backing) {
  return MODES.filter((m) => m.key === "instrumental" || (m.key === "partner" ? backing?.arrangement_type === "duet" && backing?.partner : Boolean(backing?.vocals)));
}

/** What plays under your voice, an optional quiet guide in your headphones, and who is singing. */
export default function SingOptions({ backing, mode, onMode, guide, onGuide, profiles, singerId, onSinger, disabled }) {
  const available = availableModes(backing);
  return (
    <div className="space-y-5">
      <div>
        <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.18em] text-white/55">Sing over</p>
        <div role="radiogroup" className="grid gap-2 sm:grid-cols-3">
          {available.map((m) => (
            <button key={m.key} type="button" role="radio" aria-checked={mode === m.key} data-testid={`sing-mode-${m.key}`} disabled={disabled} onClick={() => onMode(m.key)}
              className={`rounded-2xl border px-3.5 py-3 text-left transition-colors duration-200 disabled:opacity-50 ${mode === m.key ? "border-rose-300/60 bg-rose-500/15 text-white" : "border-white/10 bg-white/[0.03] text-white/70 hover:bg-white/[0.07]"}`}>
              <p className="font-display text-[13px] font-semibold">{m.label}</p>
              <p className="mt-0.5 text-[11px] leading-snug text-white/50">{m.hint}</p>
            </button>
          ))}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-x-8 gap-y-4">
        {backing?.vocals && mode !== "full" ? (
          <label className="flex cursor-pointer items-center gap-3">
            <Switch data-testid="sing-guide-toggle" checked={guide} onCheckedChange={onGuide} />
            <span className="text-[13px] text-white/75">Quiet AI guide vocal <span className="text-white/40">(only in your headphones, not in the saved song)</span></span>
          </label>
        ) : null}
        <label className="block min-w-[220px]">
          <span className="mb-1 block font-mono text-[10px] uppercase tracking-[0.16em] text-white/55">Who is singing?</span>
          <select data-testid="sing-singer-select" value={singerId || ""} disabled={disabled} onChange={(e) => onSinger(e.target.value || null)} className="w-full rounded-xl border border-white/10 bg-black/50 px-3 py-2 text-sm text-white focus:border-rose-300/60 focus:outline-none">
            {(profiles || []).map((p) => <option key={p.id} value={p.id}>{shortVoiceName(p.name)}{p.is_main ? " (main)" : ""}</option>)}
          </select>
        </label>
      </div>
    </div>
  );
}
